import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, AlertCircle, CheckCircle, Loader,
  ShieldCheck, FileText,
} from 'lucide-react';
import { callRedeemPaymentLink } from '../../services/billing';

const PaymentLinkRedeem: React.FC = () => {
  const { linkToken } = useParams<{ linkToken: string }>();
  const formRef = useRef<HTMLFormElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<{
    mode: 'subscription' | 'custom';
    productId: string;
    planId: string;
    itemName: string;
    amount: number;
    notes: string;
  } | null>(null);
  const [actionUrl, setActionUrl] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!linkToken) {
      setError('Invalid payment link');
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const result = await callRedeemPaymentLink({ linkToken });
        if (mounted) {
          setLinkData(result.linkData);
          setActionUrl(result.actionUrl);
          setFields(result.fields);
        }
      } catch (err: any) {
        if (mounted) setError(err?.message || 'Invalid or expired payment link');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [linkToken]);

  const handlePay = () => {
    setSubmitting(true);
    setTimeout(() => formRef.current?.submit(), 50);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div
          className="glass-effect rounded-2xl p-8 text-center max-w-md mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 dark:text-gray-300">Validating payment link...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div
          className="glass-effect rounded-2xl p-8 max-w-md mx-auto text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Payment Link Error</h2>
          <p className="text-slate-600 dark:text-gray-400 mb-6">{error}</p>
          <motion.a
            href="/dashboard"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary inline-block"
          >
            Go to Dashboard
          </motion.a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
      <motion.div
        className="glass-effect rounded-2xl shadow-glass p-8 max-w-lg mx-auto w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Payment Request</h2>
          <p className="text-slate-600 dark:text-gray-400">
            You've received a payment link from BioAxis
          </p>
        </div>

        {/* Payment Details */}
        {linkData && (
          <div className="space-y-4 mb-8">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-gray-800/50 space-y-3">
              {linkData.mode === 'subscription' ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Product</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white capitalize">
                      {linkData.productId}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-gray-400">Plan</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white capitalize">
                      {linkData.planId}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-gray-400">Item</span>
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    {linkData.itemName || 'Custom Payment'}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-gray-700">
                <span className="text-sm font-medium text-slate-700 dark:text-gray-300">
                  {linkData.mode === 'subscription' ? 'Monthly Amount' : 'Amount'}
                </span>
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  R{linkData.amount.toFixed(2)}
                </span>
              </div>
            </div>

            {linkData.notes && (
              <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 flex items-start gap-2">
                <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">Note from admin</span>
                  <p className="text-sm text-blue-600 dark:text-blue-300 mt-0.5">{linkData.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pay Button */}
        <motion.button
          onClick={handlePay}
          disabled={submitting || !actionUrl}
          whileHover={{ scale: submitting ? 1 : 1.02 }}
          whileTap={{ scale: submitting ? 1 : 0.98 }}
          className="w-full py-4 px-6 rounded-xl bg-primary-500 text-white font-semibold text-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {submitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Redirecting to PayFast...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              {linkData?.mode === 'custom' ? `Pay R${linkData.amount.toFixed(2)}` : 'Pay Now'}
            </>
          )}
        </motion.button>

        {submitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-700 dark:text-green-300">Redirecting to secure checkout...</span>
          </motion.div>
        )}

        <p className="text-xs text-center text-slate-400 dark:text-gray-500 mt-6">
          Payments are processed securely via PayFast
        </p>

        {/* Hidden PayFast form */}
        {actionUrl && (
          <form ref={formRef} action={actionUrl} method="POST" className="hidden">
            {Object.entries(fields).map(([k, v]) => (
              <input key={k} type="hidden" name={k} value={v} />
            ))}
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentLinkRedeem;
